import type { SubsidiaryTreeNode } from '../../../DataProvider/subsidiaries/expand-subsidiaries';

const DEFAULT_MIN_SHARE = 0.02;
const MIN_SHOWN_CHILDREN = 3;
const OTHER_RCID = -1;

/**
 * The thinnest band a company may be drawn at: 2px of fill and the 1px
 * separator under it. Anything thinner is folded into Other, because a run of
 * 1px slivers with no gap between them reads as one large company.
 */
const MIN_SUBSIDIARY_BAND_PX = 3;

export type SubsidiarySizeTransform = 'none' | 'sqrt';

export type SubsidiaryBucketOptions = {
  minShare?: number;
  /** Pixel height the view is drawn at; 0 skips the minimum-band check. */
  height?: number;
  sizeTransform?: SubsidiarySizeTransform;
};

const formatCount = new Intl.NumberFormat('en-US').format;

const subsidiaryOtherLabel = (count: number) =>
  count === 1 ? '1 other' : `${formatCount(count)} others`;

/** The companies a drill into this node lays out: an Other's members. */
export const subsidiaryDrillChildren = (node: SubsidiaryTreeNode) =>
  node.isOther === true ? (node.members ?? node.children) : node.children;

const sumTotals = (nodes: readonly SubsidiaryTreeNode[]) =>
  nodes.reduce((sum, node) => sum + node.total, 0);

/** The value the icicle partitions a node by: its total, or its children's. */
const layoutSize = (node: SubsidiaryTreeNode) =>
  Math.max(node.total, sumTotals(node.children));

type Band = { px: number; value: number };

const bandWeight = (
  node: SubsidiaryTreeNode,
  sizeTransform: SubsidiarySizeTransform
) =>
  sizeTransform === 'sqrt' ? Math.sqrt(layoutSize(node)) : layoutSize(node);

/**
 * Mirrors the replots icicle layout: children split their parent's band by
 * value, and under `sqrt` each child wants √value, scaled down only when the
 * siblings together outgrow the parent.
 */
function childBands(
  parent: Band,
  children: readonly SubsidiaryTreeNode[],
  sizeTransform: SubsidiarySizeTransform
) {
  const wanted = children.reduce(
    (sum, child) => sum + bandWeight(child, sizeTransform),
    0
  );
  const scale =
    sizeTransform === 'sqrt' && wanted > parent.value && wanted > 0
      ? parent.value / wanted
      : 1;
  return (node: SubsidiaryTreeNode): Band => {
    const value = bandWeight(node, sizeTransform) * scale;
    return {
      value,
      px: parent.value > 0 ? (parent.px * value) / parent.value : 0,
    };
  };
}

const otherOf = (
  parent: SubsidiaryTreeNode,
  members: SubsidiaryTreeNode[]
): SubsidiaryTreeNode => ({
  rcid: OTHER_RCID,
  name: subsidiaryOtherLabel(members.length),
  total: sumTotals(members),
  hasSubsidiaries: true,
  depth: parent.depth + 1,
  isOther: true,
  members,
  children: members.flatMap((member) => member.children),
});

function splitTail(
  children: readonly SubsidiaryTreeNode[],
  threshold: number,
  bandOf: ((node: SubsidiaryTreeNode) => Band) | null,
  keepOne: boolean
) {
  const descending = [...children].sort((a, b) => b.total - a.total);
  const tooThin = new Set(
    bandOf === null
      ? []
      : descending.filter((child) => bandOf(child).px < MIN_SUBSIDIARY_BAND_PX)
  );
  const largest = descending[0];
  if (keepOne && largest !== undefined && tooThin.size === descending.length) {
    tooThin.delete(largest);
  }
  const candidates = descending
    .slice(MIN_SHOWN_CHILDREN)
    .filter((child) => child.total < threshold && !tooThin.has(child))
    .reverse();
  const cap = largest?.total ?? 0;
  const folded = new Set(tooThin);
  let sum = sumTotals([...tooThin]);
  for (const child of candidates) {
    sum += child.total;
    if (sum > cap) break;
    folded.add(child);
  }
  if (folded.size < 2) return { kept: [...children], tail: [] };
  return {
    kept: children.filter((child) => !folded.has(child)),
    tail: children.filter((child) => folded.has(child)),
  };
}

/**
 * Buckets a view for drawing. Past the three largest companies at each level,
 * those under `minShare` of the company in view fold into an Other no larger
 * than the biggest company beside it, and every company too thin to draw as
 * its own band folds in regardless. An Other keeps its members raw so a drill
 * can re-bucket them, and lays their own subsidiaries out as its children so
 * the levels behind it stay visible.
 */
export function bucketSubsidiaryView(
  root: SubsidiaryTreeNode,
  {
    minShare = DEFAULT_MIN_SHARE,
    height = 0,
    sizeTransform = 'none',
  }: SubsidiaryBucketOptions = {}
): SubsidiaryTreeNode {
  let shown = root;
  while (shown.children.length === 1 && shown.children[0]?.children.length) {
    shown = shown.children[0];
  }
  const threshold = shown.total * minShare;

  const bandsFor = (node: SubsidiaryTreeNode, band: Band | null) => {
    if (height <= 0) return null;
    if (node === shown) {
      const value = node.children.reduce(
        (sum, child) => sum + bandWeight(child, sizeTransform),
        0
      );
      return childBands({ px: height, value }, node.children, sizeTransform);
    }
    return band === null
      ? null
      : childBands(band, node.children, sizeTransform);
  };

  const visit = (
    node: SubsidiaryTreeNode,
    band: Band | null
  ): SubsidiaryTreeNode => {
    if (node.children.length === 0) return node;
    const bandOf = bandsFor(node, band);
    const { kept, tail } = splitTail(
      node.children,
      threshold,
      bandOf,
      node === shown
    );
    const children = kept.map((child) => visit(child, bandOf?.(child) ?? null));
    if (tail.length > 0) {
      const other = otherOf(node, tail);
      children.push(visit(other, bandOf?.(other) ?? null));
    }
    return { ...node, children };
  };
  return visit(root, null);
}
